import { useEffect, useState } from 'react'
import { View, Text, Image } from 'react-native-web'

type Route = 'home' | 'support' | 'privacy'

function useHashRoute(): [Route, (r: Route) => void] {
  const [route, setRoute] = useState<Route>(() => {
    const hash = window.location.hash.replace('#', '') as Route
    return (hash || 'home') as Route
  })

  const updateCanonicalUrl = (currentRoute: Route) => {
    const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
    if (canonicalLink) {
      const baseUrl = 'https://www.spiritcoms.com'
      const canonicalUrl = currentRoute === 'home' ? baseUrl : `${baseUrl}#${currentRoute}`
      canonicalLink.href = canonicalUrl
    }
  }

  useEffect(() => {
    const onHashChange = () => {
      const next = window.location.hash.replace('#', '') as Route
      const newRoute = (next || 'home') as Route
      setRoute(newRoute)
      updateCanonicalUrl(newRoute)
    }
    window.addEventListener('hashchange', onHashChange)

    // Set initial canonical URL
    updateCanonicalUrl(route)

    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const navigate = (r: Route) => {
    if (!r) return
    if (r === 'home') window.location.hash = ''
    else window.location.hash = r
    updateCanonicalUrl(r)
  }

  return [route, navigate]
}

function Landing({ navigate }: { navigate: (r: Route) => void }) {
  return (
    <View style={styles.pageRoot}>
      <View style={styles.hero}>
        <Image source={{ uri: '/hero.png' }} style={styles.heroImg} resizeMode="cover" />
        <View style={styles.overlay}>
          <Text style={styles.title}>Spirit Communicate App</Text>
          <Text style={styles.subtitle}>Séance and Rituals for Communication with the Deceased</Text>
        </View>
        <View style={styles.badges}>
          <a href="https://play.google.com/store/apps/details?id=com.spiritcoms.communicate&pli=1" aria-label="Google Play" style={{ display: 'inline-block' }}>
            <img src="/play-badge.png" alt="Get it on Google Play" style={{ height: '56px', width: 'auto', display: 'block' }} />
          </a>
          <a href="https://apps.apple.com/us/app/spirit-communicate/id6751857719" aria-label="App Store" style={{ display: 'inline-block' }}>
            <img src="/appstore-badge.png" alt="Download on the App Store" style={{ height: '56px', width: 'auto', display: 'block' }} />
          </a>
        </View>
      </View>

      <View style={styles.footerLinks}>        
        <button style={styles.linkButton} onClick={() => navigate('support')}>Support</button>
        <button style={styles.linkButton} onClick={() => navigate('privacy')}>Privacy</button>
      </View>
    </View>
  )
}


function PrivacyPage({ navigate }: { navigate: (r: Route) => void }) {
  const [text, setText] = useState<string>('')
  useEffect(() => {
    fetch('/privacy.txt').then(r => r.text()).then(setText).catch(() => setText(''))
  }, [])
  return (
    <View style={[styles.centerPage, { alignItems: 'stretch' as const, maxWidth: 900, margin: '0 auto' }]}>
      <Text style={styles.header}>Privacy Policy</Text>
      {text ? (
        <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'left', lineHeight: 1.5 as any }}>{text}</pre>
      ) : (
        <Text style={{ opacity: 0.8 }}>Loading…</Text>
      )}
      <button style={styles.backButton} onClick={() => navigate('home')}>← Back</button>
    </View>
  )
}

export default function App() {
  const [route, navigate] = useHashRoute()

  if (route === 'support') return <SupportPage navigate={navigate} />
  if (route === 'privacy') return <PrivacyPage navigate={navigate} />

  return <Landing navigate={navigate} />
}

function SupportPage({ navigate }: { navigate: (r: Route) => void }) {
  const [isWide, setIsWide] = useState<boolean>(() => typeof window !== 'undefined' ? window.innerWidth >= 800 : true)
  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 800)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    <View style={[styles.centerPage, { alignItems: 'stretch' as const, maxWidth: 1000, margin: '0 auto' }]}>
      <Text style={styles.header}>Support</Text>
      <View style={[styles.supportGrid, { flexDirection: isWide ? 'row' as const : 'column' as const }] }>
        <View style={[styles.faqCard, { flex: isWide ? 2 : 0 }] }>
          <Text style={styles.faqTitle}>FAQ</Text>
          <View style={{ paddingLeft: 12 }}>
            <Text style={styles.faqItem}>• How do I start a session?</Text>
            <Text style={styles.faqItem}>• Why can’t I hear audio?</Text>
            <Text style={styles.faqItem}>• How do I enable camera/mic permissions?</Text>
            <Text style={styles.faqItem}>• Does this work offline?</Text>
          </View>
        </View>
        <View style={[styles.emailCard, { flex: isWide ? 1 : 0 }] }>
          <Text>Didn’t find what you need?</Text>
          <a href="mailto:support@SpiritComms.com" style={styles.emailBtn as any}>Email support@SpiritComms.com</a>
        </View>
      </View>
      <button style={styles.backButton} onClick={() => navigate('home')}>← Back</button>
    </View>
  )
}

const styles = {
  pageRoot: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#111',
    color: '#fff',
  },
  hero: {
    position: 'relative' as const,
    width: '100%',
    height: 'min(90vh, 900px)',
    overflow: 'hidden' as const,
  },
  heroImg: {
    width: '100%',
    height: '100%',
    position: 'absolute' as const,
    inset: 0 as any,
    zIndex: 0,
    filter: 'contrast(1.1) saturate(0.9) brightness(0.9)'
  },
  overlay: {
    position: 'absolute' as const,
    inset: 0 as any,
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    flexDirection: 'column' as const,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.85) 100%)',
    padding: '2rem',
    zIndex: 1
  },
  title: {
    fontSize: 48,
    fontWeight: 800,
    letterSpacing: 0.5,
    textAlign: 'center' as const,
    color: '#fff'
  },
  subtitle: {
    opacity: 0.85,
    marginTop: 8,
    fontSize: 16,
    color: '#fff'
  },
  badges: {
    position: 'absolute' as const,
    bottom: 'clamp(16px, 4vw, 48px)',
    left: 'clamp(16px, 6vw, 96px)',
    display: 'flex',
    gap: 'clamp(10px, 2vw, 20px)',
    zIndex: 2
  },
  badgeImage: {
    height: 56,
    width: 'auto'
  },
  footerLinks: {
    display: 'flex',
    gap: 16,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    padding: '24px 16px'
  },
  linkButton: {
    background: 'transparent',
    color: '#9ad0ff',
    border: '1px solid #1e3a5f',
    padding: '10px 16px',
    borderRadius: 999,
    cursor: 'pointer'
  },
  centerPage: {
    backgroundColor: '#111',
    color: '#fff',
    minHeight: '100%',
    display: 'flex',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    flexDirection: 'column' as const,
    padding: '2rem',
    textAlign: 'center' as const,
  },
  header: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 12,
    color: '#fff'
  },
  backButton: {
    marginTop: 20,
    background: '#2196F3',
    color: '#fff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: 20,
    cursor: 'pointer'
  },
  supportGrid: {
    display: 'flex',
    gap: 20,
    width: '100%'
  },
  faqCard: {
    backgroundColor: 'transparent',
  },
  faqTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 10,
    color: '#fff'
  },
  faqItem: {
    marginBottom: 6,
    color: '#fff'
  },
  emailCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
    alignItems: 'flex-start' as const,
    backgroundColor: '#151a24',
    border: '1px solid #1f2c40',
    borderRadius: 10,
    padding: 16
  },
  emailBtn: {
    display: 'inline-block',
    background: '#4CAF50',
    color: '#fff',
    textDecoration: 'none',
    padding: '10px 14px',
    borderRadius: 8
  }
} as const
